import { useApolloClient } from '@apollo/react-hooks';
import gql from 'not-graphql-tag';
import {
  QasSearchResult,
  QuickAddressSearchParams,
  PotentialAddress,
  SuggestedAddress,
  VerifyLevel,
} from '../__generated__/pge-types';
import compact from 'lodash/compact';

export const QasFragment = gql`
  fragment QasResults on QasSearchResult {
    verifyLevel
    isAddressTruncated
    suggestedAddress {
      addressLine1
      city
      state
      postal
      country
      isGeneralAddress
      isHighwayContractRoute
    }
    potentialAddresses {
      moniker
      address
      isFullAddress
      isMultipleAddress
      partialAddress
    }
  }
`;

const searchByIdQuery = gql`
  query qasSearchAddressById($moniker: String!) {
    qasSearchAddressById(params: { moniker: $moniker }) {
      ...QasResults
    }
  }
  ${QasFragment}
`;

const searchQuery = gql`
  query qasSearchAddress($params: QuickAddressSearchParams) {
    qasSearchAddress(params: $params) {
      ...QasResults
    }
  }
  ${QasFragment}
`;

export type QasResult =
  | {
      type: 'MULTIPLE';
      addresses: Array<PotentialAddress>;
      verifyLevel: VerifyLevel;
    }
  | {
      type: 'SINGLE';
      address: SuggestedAddress;
    }
  | {
      type: 'AUTO';
      address: SuggestedAddress;
    }
  | {
      type: 'NONE';
    };

export default function useQas() {
  const client = useApolloClient();

  async function search(params: QuickAddressSearchParams): Promise<QasResult> {
    const { data } = await client.query<{
      qasSearchAddress: QasSearchResult;
    }>({
      query: searchQuery,
      variables: { params },
      fetchPolicy: 'network-only',
    });
    const qasSearchResult = data?.qasSearchAddress;

    switch (qasSearchResult.verifyLevel) {
      case VerifyLevel.Verified:
      case VerifyLevel.InteractionRequired: {
        return {
          type:
            qasSearchResult.verifyLevel === VerifyLevel.Verified
              ? 'AUTO'
              : 'SINGLE',
          address: qasSearchResult.suggestedAddress!,
        } as QasResult;
      }
      case VerifyLevel.Multiple:
      case VerifyLevel.StreetPartial:
      case VerifyLevel.PremisesPartial: {
        return {
          type: 'MULTIPLE' as const,
          addresses: compact(qasSearchResult.potentialAddresses),
          verifyLevel: qasSearchResult.verifyLevel,
        } as QasResult;
      }
      default:
        return {
          type: 'NONE' as const,
        } as QasResult;
    }
  }

  async function searchById(moniker: string): Promise<SuggestedAddress> {
    const { data } = await client.query<{
      qasSearchAddressById: QasSearchResult;
    }>({
      query: searchByIdQuery,
      variables: { moniker },
      fetchPolicy: 'network-only',
    });

    const qasSearchResult = data?.qasSearchAddressById;

    if (qasSearchResult.verifyLevel !== VerifyLevel.Verified) {
      throw new Error('Searching by id should give a verified address');
    }

    return qasSearchResult.suggestedAddress!;
  }

  return {
    search,
    searchById,
  };
}
